import type {
  BoqRow,
  DiffField,
  FieldChange,
  FieldChanges,
  NumericTolerances,
  PercentageChangeReason,
} from '../../types'
import { normalizeItemCode, normalizeText, normalizeUnit } from '../normalization'

export const DEFAULT_NUMERIC_TOLERANCES: Readonly<NumericTolerances> = {
  quantity: 0.000001,
  unitPrice: 0.01,
  totalPrice: 0.01,
}

export interface PercentageChangeResult {
  value: number | null
  reason: PercentageChangeReason
}

function validTolerance(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value >= 0 ? value : fallback
}

export function resolveTolerances(tolerances: Partial<NumericTolerances> = {}): NumericTolerances {
  return {
    quantity: validTolerance(tolerances.quantity, DEFAULT_NUMERIC_TOLERANCES.quantity),
    unitPrice: validTolerance(tolerances.unitPrice, DEFAULT_NUMERIC_TOLERANCES.unitPrice),
    totalPrice: validTolerance(tolerances.totalPrice, DEFAULT_NUMERIC_TOLERANCES.totalPrice),
  }
}

export function calculatePercentageChange(
  baseline: number | null,
  target: number | null,
): PercentageChangeResult {
  if (baseline === null || target === null) return { value: null, reason: 'missing-value' }
  if (baseline === 0) return { value: null, reason: 'baseline-zero' }
  return { value: ((target - baseline) / Math.abs(baseline)) * 100, reason: 'calculated' }
}

export function safePercentageChange(
  baseline: number | null,
  target: number | null,
): number | null {
  return calculatePercentageChange(baseline, target).value
}

function numericFieldChange(
  field: DiffField,
  baselineValue: number | null,
  targetValue: number | null,
  tolerance: number,
): FieldChange {
  const bothPresent = baselineValue !== null && targetValue !== null
  const absoluteChange = bothPresent ? targetValue - baselineValue : null
  const changed =
    baselineValue === null || targetValue === null
      ? baselineValue !== targetValue
      : Math.abs(targetValue - baselineValue) > tolerance
  const percentage = calculatePercentageChange(baselineValue, targetValue)
  return {
    field,
    baselineValue,
    targetValue,
    changed,
    absoluteChange,
    percentageChange: percentage.value,
    percentageChangeReason: percentage.reason,
  }
}

function textFieldChange(
  field: DiffField,
  baselineValue: string | null,
  targetValue: string | null,
  baselineNormalized: string,
  targetNormalized: string,
): FieldChange {
  return {
    field,
    baselineValue,
    targetValue,
    changed: baselineNormalized !== targetNormalized,
    absoluteChange: null,
    percentageChange: null,
    percentageChangeReason: 'not-numeric',
  }
}

export function compareBoqRows(
  baselineRow: BoqRow | null,
  targetRow: BoqRow | null,
  tolerances: Partial<NumericTolerances> = {},
): FieldChanges {
  const resolved = resolveTolerances(tolerances)
  const baselineCode = baselineRow?.itemCode ?? null
  const targetCode = targetRow?.itemCode ?? null
  const baselineName = baselineRow?.itemName ?? null
  const targetName = targetRow?.itemName ?? null
  const baselineDescription = baselineRow?.itemDescription ?? null
  const targetDescription = targetRow?.itemDescription ?? null
  const baselineUnit = baselineRow?.unit ?? null
  const targetUnit = targetRow?.unit ?? null
  const totalPrice = numericFieldChange(
    'totalPrice',
    baselineRow?.effectiveTotalPrice ?? null,
    targetRow?.effectiveTotalPrice ?? null,
    resolved.totalPrice,
  )
  totalPrice.baselineTotalPriceSource = baselineRow?.totalPriceSource ?? 'missing'
  totalPrice.targetTotalPriceSource = targetRow?.totalPriceSource ?? 'missing'

  return {
    itemCode: textFieldChange(
      'itemCode',
      baselineCode,
      targetCode,
      normalizeItemCode(baselineCode),
      normalizeItemCode(targetCode),
    ),
    itemName: textFieldChange(
      'itemName',
      baselineName,
      targetName,
      normalizeText(baselineName),
      normalizeText(targetName),
    ),
    itemDescription: textFieldChange(
      'itemDescription',
      baselineDescription,
      targetDescription,
      normalizeText(baselineDescription),
      normalizeText(targetDescription),
    ),
    unit: textFieldChange(
      'unit',
      baselineUnit,
      targetUnit,
      normalizeUnit(baselineUnit),
      normalizeUnit(targetUnit),
    ),
    quantity: numericFieldChange(
      'quantity',
      baselineRow?.quantity ?? null,
      targetRow?.quantity ?? null,
      resolved.quantity,
    ),
    unitPrice: numericFieldChange(
      'unitPrice',
      baselineRow?.unitPrice ?? null,
      targetRow?.unitPrice ?? null,
      resolved.unitPrice,
    ),
    totalPrice,
  }
}

export function compareMatchedRows(
  baselineRow: BoqRow,
  targetRow: BoqRow,
  tolerances: Partial<NumericTolerances> = {},
): FieldChanges {
  return compareBoqRows(baselineRow, targetRow, tolerances)
}

export function hasChanges(fieldChanges: FieldChanges): boolean {
  return (Object.keys(fieldChanges) as DiffField[]).some((field) => fieldChanges[field].changed)
}

export function createEmptyFieldChanges(): FieldChanges {
  return compareBoqRows(null, null)
}
